import {
  IPriceUnderProductSchemaUI,
  IQuoteItem,
  IQuoteRespSchema,
  QuoteAmendmentVersionEnum,
  QuoteStatusEnum,
  QuoteTypeEnum,
} from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { formatInteger } from '@monetize/utils/core';
import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_QUOTE_COLORS,
  QUOTE_QUANTITY_CELL_GRID_TEMPLATE,
  QUOTE_QUANTITY_TEXT_INSET,
} from '../../constants/quotes';
import { useExperienceConfiguration } from '../../hooks';
import { QuantityText } from '../../routes/Quotes/Quote/components/QuantityText';
import { useQuoteContext } from '../../routes/Quotes/Quote/quoteContext';
import { getQuoteColors, isQuoteQtyChanged } from '../../utils';
import { MGrid } from '../Monetize';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';
import { MainSearchInputV2 } from '../Monetize/MCustomSelect/components/MainSearchInputV2';

const TIER_FILTER_FIELDS = ['title', 'searchValue'];

interface QuantityDropdownProps {
  index: number;
  quote: IQuoteRespSchema | null;
  item: IQuoteItem;
  value: number;
  isScheduledChange: boolean;
  scheduledChangePriorQty?: number;
  isAmendment: boolean;
  isRenewal: boolean;
  placeholder: string;
  isRemoved?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  orderedPricesUnderProduct: IPriceUnderProductSchemaUI[];
  /**
   * The raw tier quantity. `useRawValue` with a numeric `itemValue` means
   * MCustomSelect hands back the number itself, not a formatted string.
   */
  onChange: (value: number) => void;
}

export const QuantityDropdown: FC<QuantityDropdownProps> = React.forwardRef<
  any,
  QuantityDropdownProps
>(
  (
    {
      index,
      item,
      quote,
      isScheduledChange,
      scheduledChangePriorQty,
      isAmendment,
      isRenewal,
      value,
      isRemoved,
      isDisabled,
      orderedPricesUnderProduct,
      placeholder,
      isReadOnly,
      onChange,
    }: QuantityDropdownProps,
    ref: any,
  ) => {
    const { showCommaFormattedQty } = useExperienceConfiguration(
      ExperienceConfigurationKeys.quoteAndContractSettings,
    );
    const isNew = !isAmendment && !isRenewal;
    const isDraft = quote?.status === QuoteStatusEnum.DRAFT;
    // For amend v2 and new quotes, start in edit mode by default.
    const shouldEditByDefault =
      (!isDisabled &&
        quote?.amendmentVersion === QuoteAmendmentVersionEnum.v2) ||
      isNew;
    const [editMode, setEditMode] = useState<boolean>(shouldEditByDefault);
    const [focused, setFocused] = useState<boolean>(shouldEditByDefault);
    /** Derived, not state — as state it renders blank (placeholder) for a frame
     * before the effect populates it, and trails `value` by a render. */
    const formattedQuantity = useMemo(() => formatInteger(value), [value]);

    useEffect(() => {
      if (shouldEditByDefault) {
        setEditMode(true);
        setFocused(true);
      } else {
        setEditMode(false);
        setFocused(false);
      }
    }, [shouldEditByDefault]);

    const checkQtyChanged = isQuoteQtyChanged(
      item,
      Number(value),
      isScheduledChange
        ? scheduledChangePriorQty
        : (item.previousQuantity ?? 0),
    );
    const displayValue = showCommaFormattedQty ? formattedQuantity : value;
    const quoteType = quote?.type || QuoteTypeEnum.NEW;
    const { useAmendmentV2 } = useQuoteContext();
    const quoteColors =
      checkQtyChanged && !useAmendmentV2
        ? getQuoteColors(quoteType)
        : DEFAULT_QUOTE_COLORS;
    const styleProps = {
      fontWeight: '400',
      width: '100%',
      ...QUOTE_QUANTITY_TEXT_INSET,
      ...quoteColors,
    };

    const tierOptions = useMemo(() => {
      const product = orderedPricesUnderProduct.find(
        (prod) => prod.id === item.productId,
      );
      if (product?.prices) {
        return product.prices
          .map((price) => ({
            title: showCommaFormattedQty
              ? formatInteger(price?.to)
              : price?.to?.toString(),
            value: price.to,
            searchValue: price?.to?.toString() ?? '',
          }))
          .filter((option) => option.title !== '' && option.value !== null);
      }
      return [];
    }, [orderedPricesUnderProduct, item.productId, showCommaFormattedQty]);

    return (
      <MGrid
        onMouseEnter={() =>
          !isRemoved &&
          !isNew &&
          isDraft &&
          !isDisabled &&
          !isReadOnly &&
          setEditMode(true)
        }
        onMouseLeave={() =>
          !isRemoved &&
          !isNew &&
          isDraft &&
          !isDisabled &&
          !isReadOnly &&
          setEditMode(false)
        }
        onMouseDown={() =>
          !isRemoved &&
          !isNew &&
          isDraft &&
          !isDisabled &&
          !isReadOnly &&
          setFocused(true)
        }
        justifyContent="center"
        alignItems="center"
        gridTemplateColumns={QUOTE_QUANTITY_CELL_GRID_TEMPLATE}
        data-testid={`quantity-wrap-${index}`}
      >
        {focused || editMode ? (
          <MCustomSelect
            isLazy
            useRawValue
            ref={ref}
            items={tierOptions}
            itemTitle="title"
            itemValue="value"
            value={value}
            placeholder={value ? String(displayValue) : placeholder}
            isDisabled={isRemoved || isDisabled}
            isReadOnly={isReadOnly}
            inputProps={styleProps}
            onChange={onChange}
            useMainInputAsSearchInput
            MainInputComponent={MainSearchInputV2}
            internalFilterFields={TIER_FILTER_FIELDS}
          />
        ) : (
          <QuantityText
            value={displayValue}
            quote={quote}
            item={item}
            scheduledChangePriorQty={scheduledChangePriorQty}
            type={quoteType}
            isScheduledChange={isScheduledChange}
            isRemoved={isRemoved}
            hideDifference={useAmendmentV2}
          />
        )}
      </MGrid>
    );
  },
);
